import React, { useState } from "react";
import EventReviewApi from "../api/EventReviewApi";
import { showToast } from "../utils/showToast";
import {
  COMMENT_KINDS,
  COMMENT_VISIBILITY_TEXT,
  commentHostVisibility,
  commentKindOf,
  ratingText,
  reviewGuestCountsText,
} from "./utils/reviews";

// Full class strings per tone so Tailwind's content scan keeps them.
const KIND_CLASSES = {
  green: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  red: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  gray: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

const RatingStars = ({ rating }) => {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">{ratingText(rating)}</span>;
  }
  return (
    <span className="text-amber-500 text-sm" title={ratingText(rating)} aria-label={ratingText(rating)}>
      {"★".repeat(rating)}
      <span className="text-gray-300 dark:text-gray-600">{"★".repeat(5 - rating)}</span>
    </span>
  );
};

const ReviewComment = ({ comment, busy, disabled, onToggleHidden }) => {
  const kind = COMMENT_KINDS[commentKindOf(comment)];
  const visibility = commentHostVisibility(comment);
  const hidden = comment.hiddenFromHost === true;
  return (
    <li className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-900">
      <p className="text-sm text-gray-800 whitespace-pre-wrap dark:text-gray-200">{comment.text}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
        <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-semibold ${KIND_CLASSES[kind.tone]}`}>
          {kind.label}
        </span>
        <span className="text-gray-500 dark:text-gray-400">{COMMENT_VISIBILITY_TEXT[visibility]}</span>
        {comment.reportFiledAt && (
          <span className="text-gray-500 dark:text-gray-400">Filed as a safety case</span>
        )}
        {comment.key && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onToggleHidden(comment)}
            className="ml-auto font-medium text-primaryDark hover:underline disabled:opacity-50 dark:text-primary"
          >
            {busy ? "Saving…" : hidden ? "Unhide" : "Hide from host"}
          </button>
        )}
      </div>
    </li>
  );
};

/**
 * Each guest's answer to "How was it?", by name. For admin operators only: the
 * host sees the summary above, which never names anyone. `rows` come from
 * reviewGuestRows (utils/reviews.js), and the section only mounts this with
 * at least one.
 *
 * data-ph-block keeps these names and comments, safety reports among them, out
 * of session replays.
 */
const ReviewGuestList = ({ eventID, rows }) => {
  const [busyKey, setBusyKey] = useState(null);

  const toggleHidden = async (uid, comment) => {
    if (busyKey || !comment?.key) return;
    const hiddenFromHost = comment.hiddenFromHost !== true;
    setBusyKey(comment.key);
    try {
      await EventReviewApi.setCommentHidden({ eventId: eventID, uid, key: comment.key, hiddenFromHost });
      showToast(hiddenFromHost ? "Hidden from hosts." : "No longer hidden from hosts.", { type: "success" });
    } catch (err) {
      console.error("[ReviewGuestList] adminSetReviewComment error:", err);
      showToast(err?.message || "Could not update that comment.", { type: "error" });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section aria-label="Each guest" data-ph-block className="border-t pt-4 dark:border-gray-700">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Each guest (admins only)</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{reviewGuestCountsText(rows)}</p>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Feedback comments reach hosts without names once 3 or more guests have rated. Safety, other and
        unclassified comments stay with admins. Hide a comment to take it out of the host's view.
      </p>
      <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
        {rows.map((row) => (
          <li key={row.uid} className="py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">{row.name}</p>
              <RatingStars rating={row.rating} />
            </div>
            {row.askedText && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{row.askedText}</p>
            )}
            {row.comments.length > 0 && (
              <ul className="mt-2 space-y-2">
                {row.comments.map((comment, index) => (
                  <ReviewComment
                    key={comment.key || index}
                    comment={comment}
                    busy={!!busyKey && busyKey === comment.key}
                    disabled={!!busyKey}
                    onToggleHidden={(c) => toggleHidden(row.uid, c)}
                  />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ReviewGuestList;
